import { Helmet } from "react-helmet-async";
import { useLang } from "@/lib/i18n";
import {
  organizationSchema,
  websiteSchema,
  breadcrumbSchema,
  faqSchema,
  serviceSchema,
  projectSchema,
} from "@/lib/schemas";

// ─── Per-route SEO copy (EN + DE) ───
const seoData = {
  "/": {
    en: {
      title: "IRE Berlin | Turn German Taxes Into Assets",
      description: "Strategic real estate investment advisory for high-income expats in Germany. Tax-efficient structures, full execution, zero brokerage fees.",
      keywords: "Berlin real estate, expat investment Germany, tax optimisation, Sonderabschreibung, buy to let Berlin",
    },
    de: {
      title: "IRE Berlin | Verwandeln Sie deutsche Steuern in Vermögen",
      description: "Strategische Immobilien-Investmentberatung für Gutverdiener und Expats in Deutschland. Steuereffiziente Strukturen, vollständige Umsetzung, keine Maklergebühren.",
      keywords: "Immobilien Berlin, Investment Deutschland, Steueroptimierung, Sonderabschreibung, Kapitalanlage",
    },
  },
  "/calculator": {
    en: {
      title: "Tax-to-Asset Strategy Calculator | IRE Berlin",
      description: "Calculate your 2026 German tax burden and see how an IRE-style property strategy could redirect taxes into long-term assets.",
      keywords: "German tax calculator, real estate tax savings, Sonder-AfA calculator",
    },
    de: {
      title: "Steuer-zu-Vermögen Strategierechner | IRE Berlin",
      description: "Berechnen Sie Ihre deutsche Steuerlast 2026 und sehen Sie, wie eine IRE-Immobilienstrategie Steuern in langfristige Vermögenswerte umleiten könnte.",
      keywords: "Steuerrechner Deutschland, Immobilien Steuerersparnis, Sonder-AfA Rechner",
    },
  },
  "/services": {
    en: {
      title: "Services — Seven Strategic Pillars | IRE Berlin",
      description: "Property curation, tax structuring, financing, rental strategy, legal execution, management, and portfolio growth. Seven decisions, one tailored strategy.",
      keywords: "real estate advisory services, property investment Berlin, tax structuring Germany",
    },
    de: {
      title: "Leistungen — Sieben strategische Säulen | IRE Berlin",
      description: "Objektauswahl, Steuerstrukturierung, Finanzierung, Mietstrategie, rechtliche Umsetzung, Verwaltung und Portfoliowachstum.",
      keywords: "Immobilienberatung, Investmentberatung Berlin, Steuerstrukturierung",
    },
  },
  "/process": {
    en: {
      title: "Our Process — Seven Steps to Ownership | IRE Berlin",
      description: "From strategic fit call to full management. A guided process from qualification and strategy to acquisition and management.",
      keywords: "investment process, property acquisition Germany",
    },
    de: {
      title: "Unser Prozess — Sieben Schritte zum Eigentum | IRE Berlin",
      description: "Vom strategischen Erstgespräch bis zur vollständigen Verwaltung. Ein geführter Prozess von der Qualifikation bis zum Erwerb.",
      keywords: "Investmentprozess, Immobilienerwerb Deutschland",
    },
  },
  "/results": {
    en: {
      title: "Client Results — Real Outcomes | IRE Berlin",
      description: "Real client outcomes from the same strategic framework. Tax benefits, equity growth, and long-term asset building.",
      keywords: "real estate investment case studies, German property returns",
    },
    de: {
      title: "Kundenergebnisse — Echte Ergebnisse | IRE Berlin",
      description: "Echte Kundenergebnisse aus demselben strategischen Framework. Steuervorteile, Eigenkapitalwachstum und langfristiger Vermögensaufbau.",
      keywords: "Immobilien Fallstudien, deutsche Immobilienrenditen",
    },
  },
  "/team": {
    en: {
      title: "Leadership Team | IRE Berlin",
      description: "Meet the core leadership behind IRE Berlin. Strategy, investment analysis, execution, and client support.",
      keywords: "IRE Berlin team, real estate advisor Berlin",
    },
    de: {
      title: "Führungsteam | IRE Berlin",
      description: "Lernen Sie die Kernführung hinter IRE Berlin kennen. Strategie, Investitionsanalyse, Umsetzung und Kundenbetreuung.",
      keywords: "IRE Berlin Team, Immobilienberater Berlin",
    },
  },
  "/investment-thesis": {
    en: {
      title: "Investment Thesis — Why German Real Estate | IRE Berlin",
      description: "The IRE thesis: high-income expats in Germany systematically overpay taxes because they under-own assets. German tax law creates a legal mechanism to redirect tax burden into wealth.",
      keywords: "German real estate thesis, expat tax strategy Germany",
    },
    de: {
      title: "Investitionsthese — Warum deutsche Immobilien | IRE Berlin",
      description: "Die IRE-These: Gutverdiener in Deutschland zahlen systematisch zu viel Steuern, weil sie zu wenig Vermögen besitzen.",
      keywords: "deutsche Immobilien These, Expat Steuerstrategie",
    },
  },
  "/objekte": {
    en: {
      title: "Objekte im Angebot — Live Exposés | IRE Berlin",
      description: "Every property IRE Berlin currently curates for high-income expats — Amselweg 14B and Zechliner Straße 9a. Purchase price, yield, and status at a glance.",
      keywords: "Berlin real estate exposé, Potsdam investment properties, apartment for sale Berlin, IRE Berlin exposés",
    },
    de: {
      title: "Objekte im Angebot — Aktuelle Exposés | IRE Berlin",
      description: "Alle Immobilien, die IRE Berlin aktuell für Gutverdiener und Expats kuratiert — Amselweg 14B und Zechliner Straße 9a. Kaufpreis, Rendite und Status auf einen Blick.",
      keywords: "Immobilien Berlin Exposé, Kapitalanlage Potsdam, Wohnung kaufen Berlin, IRE Berlin Objekte",
    },
  },
  "/projects": {
    en: {
      title: "Off Market Karlshorst — Berlin-Karlshorst New-Build Investment | IRE Berlin",
      description: "100 KfW 40 NH apartments in Berlin's second-fastest-growing district. The Off Market Karlshorst investment case: macro thesis, asset details, and interactive return modelling.",
      keywords: "Off Market Karlshorst, Berlin Karlshorst, KfW 40 NH, new build investment",
    },
    de: {
      title: "Off Market Karlshorst — Berlin-Karlshorst Neubau-Investment | IRE Berlin",
      description: "100 KfW 40 NH Wohnungen in Berlins am zweitschnellsten wachsendem Stadtteil. Der Off Market Karlshorst Investment-Case: Makro-These, Objektdetails und interaktive Renditeberechnung.",
      keywords: "Off Market Karlshorst, Berlin Karlshorst, KfW 40 NH, Neubau Investment",
    },
  },
  "/projects/baumhaselring-180": {
    en: {
      title: "Baumhaselring 180 — Potsdam-Golm Campus Investment | IRE Berlin",
      description: "A vacant three-room apartment at the Eiche-Golm interface. Campus-adjacent demand from the University of Potsdam and Brandenburg's largest science location — with modernization-led value creation.",
      keywords: "Baumhaselring 180, Potsdam-Golm investment, WG-Wohnung Potsdam, Sanierung investment Germany",
    },
    de: {
      title: "Baumhaselring 180 — Potsdam-Golm Kapitalanlage | IRE Berlin",
      description: "Eine leerstehende Drei-Zimmer-Wohnung am Eiche-Golm-Übergang. Campus-nahe Nachfrage von der Universität Potsdam und Brandenburgs größtem Wissenschaftsstandort — mit Wertschöpfung durch Modernisierung.",
      keywords: "Baumhaselring 180, Potsdam-Golm Investment, WG-Wohnung Potsdam, Sanierung Kapitalanlage",
    },
  },
  "/projects/zechliner-strasse-9a": {
    en: {
      title: "Zechliner Straße 9a — Berlin-Mitte Altbau Investment | IRE Berlin",
      description: "108.5 m² 4-room Altbau apartment in Berlin-Gesundbrunnen. 1908 stucco façade, 5.45% gross yield at €2,500 target rent. Two U-Bahn lines, Panke corridor, protected streetscape.",
      keywords: "Zechliner Straße Berlin, Gesundbrunnen Altbau investment, Berlin Mitte Kapitalanlage, Altbau Sanierung Berlin",
    },
    de: {
      title: "Zechliner Straße 9a — Berlin-Mitte Altbau Kapitalanlage | IRE Berlin",
      description: "108,5 m² 4-Zimmer-Altbau-Wohnung in Berlin-Gesundbrunnen. Stuck-Fassade von 1908, 5,45 % Bruttorendite bei €2.500 Soll-Miete. Zwei U-Bahn-Linien, Panke-Grünzug, geschützter Straßenzug.",
      keywords: "Zechliner Straße Berlin, Gesundbrunnen Altbau, Berlin Mitte Kapitalanlage, Altbau Sanierung Berlin",
    },
  },
  "/apply": {
    en: {
      title: "Apply — Strategic Fit Assessment | IRE Berlin",
      description: "Before you apply, understand what we actually solve. A tax-optimized residential real estate structure for your income, capital base, and long-term goals.",
      keywords: "strategic fit assessment, expat real estate Germany application",
    },
    de: {
      title: "Bewerben — Strategische Passungsbewertung | IRE Berlin",
      description: "Bevor Sie sich bewerben, verstehen Sie, was wir tatsächlich lösen. Eine steueroptimierte Immobilienstruktur für Ihr Einkommen und Ihre Ziele.",
      keywords: "Strategische Passungsbewertung, Expat Immobilien Deutschland Bewerbung",
    },
  },
  "/impressum": {
    en: { title: "Impressum | IRE Berlin", description: "Legal information for IRE Berlin GmbH." },
    de: { title: "Impressum | IRE Berlin", description: "Rechtliche Informationen der IRE Berlin GmbH." },
  },
  "/datenschutz": {
    en: { title: "Privacy Policy | IRE Berlin", description: "Privacy policy and data protection information for IRE Berlin." },
    de: { title: "Datenschutzerklärung | IRE Berlin", description: "Datenschutzerklärung und Informationen zum Datenschutz bei IRE Berlin." },
  },
  "/agb": {
    en: { title: "Terms & Conditions | IRE Berlin", description: "General terms and conditions of IRE Berlin GmbH." },
    de: { title: "AGB | IRE Berlin", description: "Allgemeine Geschäftsbedingungen der IRE Berlin GmbH." },
  },
};

const defaultSeo = {
  en: { title: "IRE Berlin | Strategic Real Estate Advisory", description: "Strategic real estate investment for high-income expats in Germany." },
  de: { title: "IRE Berlin | Strategische Immobilienberatung", description: "Strategische Immobilieninvestition für Gutverdiener in Deutschland." },
};

// Map per-route → which crawl-relevant schemas to inject in addition to Organization + WebSite
const SCHEMA_INJECT_PER_ROUTE = {
  "/": ["org", "website"],
  "/services": ["service"],
  "/projects": ["project"],
};

// Static FAQ content per route — used for FAQPage JSON-LD so Google can show Q&A rich results.
// Keep these mirrored with the on-page FAQ component texts.
const ROUTE_FAQS = {
  "/projects": [
    {
      question: "Why buy now if it's not finished until late 2028?",
      answer: "The longer construction timeline is already reflected in the purchase price — this property is priced below what comparable finished quality would cost today. Payments follow construction progress under MABV, so interest only accrues on amounts actually disbursed.",
    },
    {
      question: "What about construction-period interest?",
      answer: "Construction-period interest applies only to disbursed amounts — not the full purchase price. For investors with rental intent, these costs are tax-relevant as Werbungskosten via Anlage V. We document rental intention in the purchase contract from day one.",
    },
    {
      question: "What if the developer runs into problems?",
      answer: "Grimm Holding holds sufficient equity to complete the project regardless of how many units are sold. Unsold units go into Grimm's own rental portfolio — standard practice across their 35-year track record. You are not buying into a project that only continues if enough buyers pay in.",
    },
    {
      question: "What if I wait six months and buy when it's closer to completion?",
      answer: "You can. But today you choose your floor, orientation, layout and position within the building. Over a 10-year hold, the right unit — better floor, better aspect, better rentability — matters far more than saving a small amount of construction-period interest.",
    },
  ],
  "/calculator": [
    {
      question: "How accurate are the 2026 German tax brackets used here?",
      answer: "We use the 2026 federal income-tax brackets including the solidarity surcharge thresholds. Church tax is optional and toggled by the user. The calculator is illustrative and not a substitute for a Steuerberater's specific advice.",
    },
    {
      question: "What is Sonder-AfA (§7b EStG) and why does it matter?",
      answer: "Sonder-AfA is a special depreciation rule for new-build energy-efficient residential property: 5% of qualifying construction costs per year for 4 years, stacked on top of the regular linear depreciation. For high-tax-bracket investors it is one of the largest legal tax-redirecting mechanisms in German law.",
    },
    {
      question: "Does the model assume I am a German tax resident?",
      answer: "Yes. The calculator models a German-tax-resident household — the typical IRE Berlin client (high-income expats and Gutverdiener on EU/non-EU contracts in Germany). Talk to us if your situation is more complex.",
    },
  ],
};

const SITE_URL = "https://ireberlin.com";

const SEO = ({ path, faqs = null, breadcrumbs = null, noIndex = false }) => {
  const { lang } = useLang();
  const pageData = seoData[path] || defaultSeo;
  const data = pageData[lang] || pageData.en;
  const cleanPath = path === "/" ? "" : path;
  const canonical = `${SITE_URL}${cleanPath}`;
  const enUrl = `${SITE_URL}${cleanPath}`;
  const deUrl = `${SITE_URL}${cleanPath}?lang=de`;

  // Build JSON-LD payload
  const ldGraph = [];
  // Always include Organisation + WebSite on every page (single canonical id)
  ldGraph.push(organizationSchema(lang));
  ldGraph.push(websiteSchema());

  const inject = SCHEMA_INJECT_PER_ROUTE[path] || [];
  if (inject.includes("service")) ldGraph.push(serviceSchema(lang));
  if (inject.includes("project")) ldGraph.push(projectSchema(lang));

  if (Array.isArray(faqs) && faqs.length > 0) {
    ldGraph.push(faqSchema(faqs));
  } else if (ROUTE_FAQS[path]) {
    // Fallback: use the per-route static FAQ list defined above
    ldGraph.push(faqSchema(ROUTE_FAQS[path]));
  }
  if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
    ldGraph.push(breadcrumbSchema(breadcrumbs));
  }

  return (
    <Helmet>
      <html lang={lang} />
      <title>{data.title}</title>
      <meta name="description" content={data.description} />
      {data.keywords && <meta name="keywords" content={data.keywords} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonical} />
      {/* hreflang: EN canonical + DE alternate + x-default */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="de" href={deUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />
      {/* Open Graph */}
      <meta property="og:type" content={path === "/projects" ? "article" : "website"} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={data.title} />
      <meta property="og:description" content={data.description} />
      <meta property="og:image" content="https://ireberlin.com/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={lang === "de"
        ? `IRE Berlin — ${data.title.replace(" | IRE Berlin", "")} · Vorschau`
        : `IRE Berlin — ${data.title.replace(" | IRE Berlin", "")} · social preview`} />
      <meta property="og:site_name" content="IRE Berlin" />
      <meta property="og:locale" content={lang === "de" ? "de_DE" : "en_US"} />
      <meta property="og:locale:alternate" content={lang === "de" ? "en_US" : "de_DE"} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={data.title} />
      <meta name="twitter:description" content={data.description} />
      <meta name="twitter:image" content="https://ireberlin.com/og-image.jpg" />
      <meta name="twitter:image:alt" content={lang === "de"
        ? `IRE Berlin — ${data.title.replace(" | IRE Berlin", "")}`
        : `IRE Berlin — ${data.title.replace(" | IRE Berlin", "")}`} />
      {/* JSON-LD structured data (one <script> per node — Google reads either form) */}
      {ldGraph.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
};

export default SEO;
